import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronRight, BookOpen, Video, FileText, Layers, Activity, FlaskConical, Ribbon, Stethoscope } from 'lucide-react';
import axios from 'axios';
import { calculateReadingTime } from '../data/educationCategories';
import { getAllAnatomyPages } from '../data/anatomyPages';
import { afterVisitInstructions, afterVisitContent } from '../data/afterVisitContent';
import { symptomsData } from '../data/symptomsContent';
import { testsData } from '../data/testsContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Synonym mapping: patient language → medical terms
const symptomSynonyms = {
  // Urination difficulty
  "hard to pee": ["urinary retention", "bph", "weak stream", "voiding", "obstruction"],
  "can't pee": ["urinary retention", "acute retention", "catheter", "obstruction"],
  "trouble peeing": ["urinary retention", "bph", "weak stream", "voiding difficulty"],
  "difficult to urinate": ["urinary retention", "bph", "obstruction", "voiding"],
  "slow stream": ["weak stream", "bph", "prostate", "flow"],
  "weak pee": ["weak stream", "bph", "prostate", "uroflow"],
  "dribbling": ["post-void dribbling", "bph", "incontinence", "prostate"],
  "stop and start": ["intermittent stream", "bph", "prostate"],
  "pushing to pee": ["straining", "bph", "retention", "voiding"],
  
  // Frequency/urgency
  "pee a lot": ["frequent urination", "overactive bladder", "oab", "frequency"],
  "always have to pee": ["frequent urination", "oab", "urgency", "overactive"],
  "peeing all the time": ["frequency", "oab", "overactive bladder", "polyuria"],
  "can't hold it": ["urgency", "urge incontinence", "oab", "overactive"],
  "sudden urge": ["urgency", "oab", "overactive bladder", "urge incontinence"],
  "rushing to bathroom": ["urgency", "oab", "urge incontinence"],
  "up at night to pee": ["nocturia", "bph", "oab", "night time"],
  "waking up to pee": ["nocturia", "night", "bph", "prostate"],
  
  // Incontinence
  "leaking urine": ["incontinence", "leakage", "stress incontinence", "urge incontinence"],
  "wetting myself": ["incontinence", "leakage", "accidents"],
  "accidents": ["incontinence", "leakage", "urgency"],
  "leak when i cough": ["stress incontinence", "leakage", "pelvic floor"],
  "leak when i sneeze": ["stress incontinence", "leakage", "pelvic floor"],
  "leak when i laugh": ["stress incontinence", "leakage", "pelvic floor"],
  "leak when i exercise": ["stress incontinence", "leakage", "pelvic floor"],
  "pee myself": ["incontinence", "leakage", "accidents"],
  
  // Pain
  "burning when i pee": ["dysuria", "uti", "urinary tract infection", "painful urination"],
  "hurts to pee": ["dysuria", "uti", "painful urination", "burning"],
  "pain when peeing": ["dysuria", "uti", "painful urination"],
  "stinging": ["dysuria", "uti", "burning", "urethritis"],
  "balls hurt": ["testicular pain", "testicle", "epididymitis", "orchitis"],
  "testicle hurts": ["testicular pain", "epididymitis", "orchitis", "torsion"],
  "pain in testicle": ["testicular pain", "epididymitis", "scrotal"],
  "groin pain": ["testicular", "hernia", "kidney stone", "inguinal"],
  "side pain": ["flank pain", "kidney stone", "kidney", "renal"],
  "back pain kidney": ["flank pain", "kidney stone", "pyelonephritis", "renal"],
  "lower belly pain": ["pelvic pain", "bladder", "prostatitis"],
  "pelvic pain": ["pelvic", "bladder pain", "interstitial cystitis", "prostatitis"],
  
  // Blood
  "blood in pee": ["hematuria", "blood in urine", "bleeding"],
  "blood in urine": ["hematuria", "bleeding", "gross hematuria"],
  "pink urine": ["hematuria", "blood", "bleeding"],
  "red urine": ["hematuria", "blood", "gross hematuria"],
  "peeing blood": ["hematuria", "blood in urine", "bleeding"],
  "blood in semen": ["hematospermia", "blood ejaculate"],
  
  // Sexual/erectile
  "can't get hard": ["erectile dysfunction", "ed", "impotence", "erection"],
  "can't get it up": ["erectile dysfunction", "ed", "impotence"],
  "erection problems": ["erectile dysfunction", "ed", "impotence"],
  "trouble getting hard": ["erectile dysfunction", "ed", "erection"],
  "soft erection": ["erectile dysfunction", "ed", "erection quality"],
  "losing erection": ["erectile dysfunction", "ed", "erection"],
  "no morning wood": ["erectile dysfunction", "ed", "testosterone", "low t"],
  "low sex drive": ["low testosterone", "libido", "hypogonadism"],
  "no libido": ["low testosterone", "libido", "sexual desire"],
  "curved penis": ["peyronie", "curvature", "bent penis"],
  "bent penis": ["peyronie", "curvature", "penile"],
  "painful erection": ["peyronie", "priapism"],
  "tight foreskin": ["phimosis", "foreskin", "circumcision"],
  "foreskin won't pull back": ["phimosis", "foreskin", "tight"],
  
  // Fertility
  "can't have kids": ["infertility", "male infertility", "fertility", "sperm"],
  "trying to conceive": ["infertility", "fertility", "sperm", "semen analysis"],
  "low sperm count": ["infertility", "oligospermia", "semen analysis", "sperm"],
  "no sperm": ["azoospermia", "infertility", "semen analysis"],
  
  // Infections
  "uti": ["urinary tract infection", "bladder infection", "cystitis"],
  "bladder infection": ["uti", "urinary tract infection", "cystitis"],
  "kidney infection": ["pyelonephritis", "uti", "upper tract"],
  "keep getting utis": ["recurrent uti", "frequent infections", "prevention"],
  "utis keep coming back": ["recurrent uti", "prevention", "prophylaxis"],
  
  // Prostate
  "prostate problems": ["bph", "prostatitis", "prostate cancer", "psa"],
  "enlarged prostate": ["bph", "benign prostatic hyperplasia", "prostate"],
  "prostate pain": ["prostatitis", "chronic pelvic pain", "cpps"],
  "prostate cancer": ["prostate cancer", "psa", "biopsy", "screening"],
  
  // Kidney stones
  "kidney stone": ["kidney stone", "renal calculus", "nephrolithiasis", "stone"],
  "passing a stone": ["kidney stone", "ureteral stone", "stone passage"],
  "stone pain": ["kidney stone", "renal colic", "flank pain"],
  
  // General
  "down there problems": ["urologic", "genital", "pelvic"],
  "private parts": ["genital", "urologic", "pelvic"],
  "waterworks": ["urinary", "bladder", "voiding"],
  
  // Lumps/swelling
  "lump on testicle": ["testicular mass", "testicular cancer", "hydrocele", "spermatocele"],
  "swollen testicle": ["hydrocele", "epididymitis", "orchitis", "scrotal swelling"],
  "bump down there": ["mass", "cyst", "lump"],
  "veins on testicle": ["varicocele", "scrotal", "veins"],
  
  // Post-procedure
  "after surgery": ["after visit", "post-operative", "recovery"],
  "recovery": ["after visit", "post-procedure", "aftercare"],
  "what to expect after": ["after visit", "post-operative", "recovery"]
};

const UnifiedSearch = ({ placeholder = "Search all topics...", variant = "default" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Load content from API
  useEffect(() => {
    const loadContent = async () => {
      try {
        const [articlesRes, videosRes] = await Promise.all([
          axios.get(`${API}/blog?limit=100`),
          axios.get(`${API}/videos`)
        ]);
        setArticles(articlesRes.data);
        setVideos(videosRes.data);
      } catch (error) {
        console.error("Error loading content:", error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine if article belongs in cancers hub
  const isCancerArticle = (title) => {
    const cancerKeywords = ['Cancer', 'Tumor', 'Carcinoma', 'Active Surveillance', 'PSA', 'Prostatectomy vs Radiation', 'Proton Beam', 'Photon Beam'];
    return cancerKeywords.some(keyword => title.includes(keyword));
  };

  // Determine if article belongs in procedures hub
  const isProcedureArticle = (title) => {
    const procedureKeywords = ['vs TURP', 'vs Aquablation', 'GreenLight', 'Prostatectomy vs', 'Radiation Therapy Side', 'Circumcision'];
    return procedureKeywords.some(keyword => title.includes(keyword));
  };

  // Build all searchable content
  const allContent = useMemo(() => {
    const content = [];

    // Add blog articles with correct paths
    articles.forEach(article => {
      const readingTime = calculateReadingTime(article.content || '');
      let path, hubType, icon;
      
      if (isCancerArticle(article.title)) {
        path = `/cancers/${article.id}`;
        hubType = 'Cancers';
        icon = Ribbon;
      } else if (isProcedureArticle(article.title)) {
        path = `/procedures/${article.id}`;
        hubType = 'Procedures';
        icon = Stethoscope;
      } else {
        path = `/conditions/${article.id}`;
        hubType = 'Conditions';
        icon = BookOpen;
      }
      
      content.push({
        id: article.id,
        title: article.title,
        type: 'article',
        icon,
        path,
        category: hubType,
        readingTime,
        searchText: `${article.title} ${article.content || ''} ${article.category || ''} ${(article.tags || []).join(' ')}`.toLowerCase(),
        titleLower: article.title.toLowerCase()
      });
    });

    // Add symptoms
    Object.entries(symptomsData).forEach(([id, symptom]) => {
      content.push({
        id,
        title: symptom.title,
        type: 'symptom',
        icon: Activity,
        path: `/symptoms/${id}`,
        category: 'Symptoms',
        searchText: `${symptom.title} ${symptom.subtitle} ${symptom.description} symptom`.toLowerCase(),
        titleLower: symptom.title.toLowerCase()
      });
    });

    // Add tests
    Object.entries(testsData).forEach(([id, test]) => {
      content.push({
        id,
        title: test.title,
        type: 'test',
        icon: FlaskConical,
        path: `/tests/${id}`,
        category: 'Tests',
        searchText: `${test.title} ${test.subtitle || ''} ${test.description || ''} test`.toLowerCase(),
        titleLower: test.title.toLowerCase()
      });
    });

    // Add videos
    videos.forEach(video => {
      content.push({
        id: video.id,
        title: video.title,
        type: 'video',
        icon: Video,
        path: '/videos',
        category: 'Video',
        duration: video.duration,
        searchText: `${video.title} ${video.description || ''} ${(video.tags || []).join(' ')} video`.toLowerCase(),
        titleLower: video.title.toLowerCase()
      });
    });

    // Add anatomy pages
    getAllAnatomyPages().forEach(page => {
      const contentText = page.simpleExplanation + ' ' + page.moreDetail;
      const readingTime = calculateReadingTime(contentText);
      content.push({
        id: page.id,
        title: page.title,
        type: 'anatomy',
        icon: Layers,
        path: `/anatomy/${page.id}`,
        category: 'Anatomy',
        readingTime,
        searchText: `${page.title} ${page.subtitle} ${page.simpleExplanation} ${page.moreDetail} anatomy`.toLowerCase(),
        titleLower: page.title.toLowerCase()
      });
    });

    // Add After Visit instructions
    afterVisitInstructions.forEach(item => {
      const itemContent = afterVisitContent[item.id];
      let searchText = `${item.title} ${item.description}`.toLowerCase();
      if (itemContent) {
        itemContent.sections?.forEach(section => {
          searchText += ` ${section.heading} ${section.content}`.toLowerCase();
        });
      }
      content.push({
        id: item.id,
        title: item.title,
        type: 'aftervisit',
        icon: FileText,
        path: `/after-visit/${item.id}`,
        category: 'After Visit',
        searchText,
        titleLower: item.title.toLowerCase()
      });
    });

    return content;
  }, [articles, videos]);

  // Expand query with synonyms
  const expandQueryWithSynonyms = (query) => {
    const queryLower = query.toLowerCase().trim();
    const expandedTerms = new Set([queryLower]);
    
    // Check each synonym phrase
    Object.entries(symptomSynonyms).forEach(([phrase, medicalTerms]) => {
      // Check if query contains the phrase or phrase contains query
      if (queryLower.includes(phrase) || phrase.includes(queryLower)) {
        medicalTerms.forEach(term => expandedTerms.add(term.toLowerCase()));
      }
      
      // Also check individual words from the phrase
      const phraseWords = phrase.split(' ');
      const queryWords = queryLower.split(' ');
      
      // If 2+ words match between query and phrase, add the medical terms
      const matchingWords = queryWords.filter(qw => 
        qw.length > 2 && phraseWords.some(pw => pw.includes(qw) || qw.includes(pw))
      );
      
      if (matchingWords.length >= 2 || 
          (queryWords.length === 1 && phraseWords.some(pw => pw === queryLower))) {
        medicalTerms.forEach(term => expandedTerms.add(term.toLowerCase()));
      }
    });
    
    return Array.from(expandedTerms);
  };

  // Improved search with relevance scoring and synonym expansion
  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/).filter(w => w.length > 1);
    
    // Get expanded search terms from synonyms
    const expandedTerms = expandQueryWithSynonyms(query);
    const allSearchTerms = [...new Set([...queryWords, ...expandedTerms])];
    
    // Score each item
    const scored = allContent.map(item => {
      let score = 0;
      
      // Exact title match (highest priority)
      if (item.titleLower === query) {
        score += 100;
      }
      // Title starts with query
      else if (item.titleLower.startsWith(query)) {
        score += 50;
      }
      // Title contains query as whole phrase
      else if (item.titleLower.includes(query)) {
        score += 30;
      }
      
      // Check original query words in title
      queryWords.forEach(word => {
        if (word.length < 2) return;
        if (item.titleLower.includes(word)) {
          score += 20;
          if (item.titleLower.startsWith(word)) {
            score += 10;
          }
        }
        if (item.searchText.includes(word)) {
          score += 5;
        }
      });
      
      // Check expanded/synonym terms (slightly lower weight than direct matches)
      expandedTerms.forEach(term => {
        if (term === query) return; // Skip original query
        if (item.titleLower.includes(term)) {
          score += 15;
        }
        if (item.searchText.includes(term)) {
          score += 8;
        }
      });
      
      // Boost certain content types based on query context
      if (query.includes('symptom') && item.type === 'symptom') score += 15;
      if (query.includes('test') && item.type === 'test') score += 15;
      if (query.includes('video') && item.type === 'video') score += 15;
      if (query.includes('after') && item.type === 'aftervisit') score += 15;
      if ((query.includes('cancer') || query.includes('tumor')) && item.category === 'Cancers') score += 15;
      
      // Boost symptoms if query sounds like a symptom description
      const symptomIndicators = ['hurt', 'pain', 'can\'t', 'hard to', 'trouble', 'problem', 'burning', 'blood', 'leak'];
      if (symptomIndicators.some(ind => query.includes(ind)) && item.type === 'symptom') {
        score += 10;
      }
      
      return { ...item, score };
    });
    
    // Filter items with score > 0 and sort by score
    return scored
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10); // Show top 10 results
  }, [allContent, searchQuery]);

  const handleResultClick = (path) => {
    setSearchQuery('');
    setIsOpen(false);
    navigate(path);
  };

  const handleInputFocus = () => {
    if (searchQuery.trim()) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setIsOpen(e.target.value.trim().length > 0);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return '#e74c3c';
      case 'anatomy': return '#9b59b6';
      case 'aftervisit': return '#0d9488';
      case 'symptom': return '#ef4444';
      case 'test': return '#f59e0b';
      default: return '#0d9488';
    }
  };

  return (
    <div className={`unified-search ${variant}`} data-testid="unified-search">
      <div className="unified-search-wrapper" ref={inputRef}>
        <Search size={20} className="unified-search-icon" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className="unified-search-input"
          data-testid="unified-search-input"
        />
        {searchQuery && (
          <button 
            className="unified-search-clear"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && filteredResults.length > 0 && (
        <div className="unified-search-dropdown" ref={dropdownRef} data-testid="search-results">
          <div className="unified-search-results">
            {filteredResults.map((item, idx) => {
              const Icon = item.icon;
              return (
                <button
                  key={`${item.type}-${item.id}-${idx}`}
                  className="unified-search-result"
                  onClick={() => handleResultClick(item.path)}
                  data-testid={`search-result-${idx}`}
                >
                  <Icon size={18} style={{ color: getTypeColor(item.type) }} />
                  <div className="unified-result-info">
                    <span className="unified-result-title">{item.title}</span>
                    <span className="unified-result-category">{item.category}</span>
                  </div>
                  <ChevronRight size={16} className="unified-result-arrow" />
                </button>
              );
            })}
          </div>
          <div className="unified-search-footer">
            {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''} found
          </div>
        </div>
      )}

      {/* No results message */}
      {isOpen && searchQuery.trim() && filteredResults.length === 0 && !loading && (
        <div className="unified-search-dropdown" ref={dropdownRef}>
          <div className="unified-search-empty">
            <p>No results for "{searchQuery}"</p>
            <span>Try different keywords</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedSearch;
